import { isValidDateString } from '@act/shared/data-transfer-objects';
import { reemaColors } from '@act/toolkit/styles/colors';
import { Box, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  Path,
  PathValue,
  RegisterOptions,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import { useFormTracking } from '../hooks';

/**
 * Parses a yyyy-MM-dd string (valid or invalid) into month, day, year for display.
 * Keeps invalid dates visible in the inputs instead of clearing them.
 */
function parseRawDateParts(value: string): {
  month: string;
  day: string;
  year: string;
} {
  if (!value) return { month: '', day: '', year: '' };

  const [y, m, d] = value.split('-');
  return {
    month: m ?? '',
    day: d ?? '',
    year: y ?? '',
  };
}

/**
 * Formats the raw parts into YYYY-MM-DD for the form state.
 * This prepends the '0' for single digits ONLY for the underlying value.
 */
function toRawDateString(month: string, day: string, year: string): string {
  if (!year && !month && !day) return '';

  const paddedMonth = month ? month.padStart(2, '0') : '';
  const paddedDay = day ? day.padStart(2, '0') : '';

  return `${year}-${paddedMonth}-${paddedDay}`;
}

export type ControlledDateOfBirthFieldProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME>;

/**
 * Date-of-birth field with separate month / day / year inputs. Value is stored as yyyy-MM-dd.
 * Validates that the value is a real calendar date (same as a native date picker would).
 *
 * - When the form uses a resolver (e.g. zodResolver), the schema must validate date-of-birth
 *   (e.g. ConsentIdentitySchema's .refine(isValidDateString)); the resolver drives field errors.
 * - When no resolver is used, this component adds that validation via the `rules` prop.
 */
export const ControlledDateOfBirthField = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
>({
  name,
  control,
  required,
  label,
  rules,
  disabled = false,
  trackingName,
  sensitive = true,
}: ControlledDateOfBirthFieldProps<FORM_TYPE, FIELD_NAME>) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState({ month: '', day: '', year: '' });
  const trackingProps = useFormTracking(trackingName, sensitive);
  const watchedValue = useWatch({ control, name });

  useEffect(() => {
    const formValue = (watchedValue as string) ?? '';
    if (formValue !== toRawDateString(local.month, local.day, local.year)) {
      setLocal(parseRawDateParts(formValue));
    }
  }, [watchedValue]);

  const dateOfBirthRules = useMemo((): RegisterOptions<
    FORM_TYPE,
    FIELD_NAME
  > => {
    const existing = rules?.validate;
    const runExisting = (
      value: PathValue<FORM_TYPE, FIELD_NAME>,
      formValues: FORM_TYPE
    ): boolean | string => {
      if (typeof existing === 'function')
        return existing(value, formValues) as boolean | string;
      if (typeof existing === 'object' && existing !== null) {
        for (const fn of Object.values(existing)) {
          const r = (
            fn as (
              v: PathValue<FORM_TYPE, FIELD_NAME>,
              f: FORM_TYPE
            ) => boolean | string
          )(value, formValues);
          if (r !== true) return r;
        }
      }
      return true;
    };
    return {
      ...rules,
      validate: (value, formValues): boolean | string => {
        const str = value as string | undefined;
        if (str && str.length >= 10 && !isValidDateString(str))
          return 'validation.invalidDateOfBirth';
        return runExisting(value, formValues);
      },
    };
  }, [rules]);

  return (
    <Controller
      control={control}
      name={name}
      rules={dateOfBirthRules}
      render={({ field, fieldState: { error } }) => {
        const handlePartChange = (
          part: 'month' | 'day' | 'year',
          val: string
        ) => {
          const clean = val.replace(/\D/g, '');
          const next = { ...local, [part]: clean };
          setLocal(next);
          field.onChange(toRawDateString(next.month, next.day, next.year));
        };

        const handleBlur = (part: 'month' | 'day' | 'year') => {
          const value = local[part];
          if (value) {
            let paddedValue = value;

            // Only pad Month and Day, and only if they are a single digit
            if ((part === 'month' || part === 'day') && value.length === 1) {
              paddedValue = value.padStart(2, '0');
            }

            const next = { ...local, [part]: paddedValue };
            setLocal(next);

            field.onChange(toRawDateString(next.month, next.day, next.year));
          }
          field.onBlur();
        };

        return (
          <FormFieldWrappers.FieldWrapper>
            <FormFieldWrappers.InputWrapper>
              {label && (
                <Typography
                  sx={{
                    color: reemaColors.black,
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}
                  variant="titleSmall"
                >
                  {label}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '16px',
                  flex: 1,
                }}
              >
                <TextField
                  {...trackingProps}
                  label={t('date.month')}
                  type="text"
                  inputProps={{
                    maxLength: 2,
                    inputMode: 'numeric',
                  }}
                  value={local.month}
                  onChange={e => handlePartChange('month', e.target.value)}
                  onFocus={event => {
                    event.target.select();
                  }}
                  onBlur={() => handleBlur('month')}
                  placeholder={t('date.monthPlaceholder')}
                  error={!!error}
                  fullWidth
                  required={required}
                  disabled={disabled}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                  }}
                />
                <Typography variant="titleMedium">/</Typography>
                <TextField
                  label={t('date.day')}
                  type="text"
                  inputProps={{
                    maxLength: 2,
                    inputMode: 'numeric',
                  }}
                  value={local.day}
                  onChange={e => handlePartChange('day', e.target.value)}
                  onBlur={() => handleBlur('day')}
                  placeholder={t('date.dayPlaceholder')}
                  error={!!error}
                  fullWidth
                  required={required}
                  disabled={disabled}
                  onFocus={event => {
                    event.target.select();
                  }}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                  }}
                />
                <Typography variant="titleMedium">/</Typography>
                <TextField
                  label={t('date.year')}
                  type="text"
                  inputProps={{
                    minLength: 4,
                    maxLength: 4,
                    inputMode: 'numeric',
                  }}
                  value={local.year}
                  onFocus={event => {
                    event.target.select();
                  }}
                  onChange={e => handlePartChange('year', e.target.value)}
                  onBlur={() => handleBlur('year')}
                  placeholder={t('date.yearPlaceholder')}
                  error={!!error}
                  fullWidth
                  required={required}
                  disabled={disabled}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                  }}
                />
              </Box>
              {error?.message && (
                <Box
                  sx={{
                    color: 'error.main',
                    typography: 'caption',
                    mt: 0.5,
                  }}
                >
                  {t(error.message)}
                </Box>
              )}
            </FormFieldWrappers.InputWrapper>
          </FormFieldWrappers.FieldWrapper>
        );
      }}
    />
  );
};
